import * as yup from 'yup';

import { Formik, FormikHelpers } from 'formik';

import { format } from 'date-fns';
import { TeamPlayerForm } from 'modules/teams/ui/components/team-card/styles';
import { DefaultDropzone } from 'ud-form/components/dropzoneV2';
import UDFormInput from 'ud-form/components/input';
import UDButton from 'ud-ui/components/button';
import { useState } from 'react';
import { birthDateFormatter } from 'ud-form/components/input/domain/formatters/birth-date-formatter';
import UDFormLabel from 'ud-form/components/label';
import { Player } from '../../domain/interfaces/player';
import { usePreventNavigation } from 'shared/hooks';
import { Pages } from 'modules/navigation/domain/enums/pages';
import _ from 'lodash';

interface FormValues {
  id?: number;
  surname: string;
  name: string;
  middleName?: string;
  birthDate: string;
  height?: string;
  weight?: string;
  photo?: (File | string)[];
}

export type SubmitPlayerForm = ({ values, formikHelpers }: { values: FormValues; formikHelpers: FormikHelpers<FormValues> }) => void;

interface FormProps {
  onSubmit: SubmitPlayerForm;
  initialValues?: Player;
  isLoading?: boolean;
}

export const teamPlayerValidator = yup.object().shape({
  name: yup.string().required('Обязательное поле'),
  surname: yup.string().required('Обязательное поле'),
  birthDate: yup
    .string()
    .matches(/^(\d{2})\.(\d{2})\.(\d{4})$/, 'Дата рождения должна быть в формате: число.месяц.год')
    .required('Обязательное поле'),
});

const PlayerForm = (props: FormProps) => {
  const { onSubmit, initialValues: initialPlayer, isLoading: isLoadingForm } = props;

  const [isFormDirty, setIsFormDirty] = useState(false);
  const [leavingConfirmed, setLeavingConfirmed] = useState(false);

  const { backHandler, PreventNavigationModal } = usePreventNavigation({
    shouldPrevent: isFormDirty && !leavingConfirmed,
    onConfirm: () => {
      setLeavingConfirmed(true);
      setIsFormDirty(false);
    },
    defaultPath: Pages.PLAYERS.INDEX,
  });

  const initialValues: FormValues = initialPlayer
    ? {
        id: initialPlayer.id,
        surname: initialPlayer.surname,
        name: initialPlayer.name,
        middleName: initialPlayer.middleName,
        birthDate: initialPlayer.birthDate ? format(new Date(initialPlayer.birthDate), 'dd.MM.yyyy') : '',
        height: initialPlayer.height?.toString(),
        weight: initialPlayer.weight?.toString(),
        photo: initialPlayer.photo ? [initialPlayer.photo.externalUrl] : [],
      }
    : {
        surname: '',
        name: '',
        middleName: '',
        birthDate: '',
      };

  return (
    <Formik
      initialValues={initialValues}
      validationSchema={teamPlayerValidator}
      onSubmit={(values, formikHelpers) => {
        onSubmit({ values, formikHelpers });
        formikHelpers.resetForm({ values }); // делаем текущие значения базой
        setIsFormDirty(false);
      }}
      validate={(values) => {
        const dirty = !_.isEqual(values, initialValues);
        if (dirty !== isFormDirty && !leavingConfirmed) {
          setIsFormDirty(dirty);
        }
        return {};
      }}
    >
      {(formProps) => {
        return (
          <form className="d-flex flex-column justify-content-between h-100" onSubmit={formProps.handleSubmit}>
            <TeamPlayerForm>
              <div className="row">
                <div className="col-6" style={{ paddingRight: 24 }}>
                  <UDFormInput name="surname" label="Фамилия" placeholder="Введите фамилию" />

                  <div className="player-data">
                    <UDFormInput name="name" label="Имя" placeholder="Введите имя" />

                    <UDFormInput name="middleName" label="Отчество" placeholder="Введите отчество" />
                  </div>

                  <div className="player-data">
                    <UDFormInput
                      name="birthDate"
                      label="Дата рождения"
                      placeholder="Укажите дату рождения"
                      mask="дд.мм.гггг"
                      formatter={birthDateFormatter}
                    />
                  </div>

                  <div className="player-data">
                    <UDFormInput name="height" label="Рост игрока" placeholder="Укажите рост" />

                    <UDFormInput name="weight" label="Вес игрока" placeholder="Укажите вес" />
                  </div>
                </div>

                <div className="col-6" style={{ paddingLeft: 24 }}>
                  <UDFormLabel className="mb-2">Фото игрока</UDFormLabel>
                  <DefaultDropzone name="photo" className="p-6" />
                </div>
              </div>

              <div className="form-buttons col-6 pr-6">
                <UDButton
                  type="submit"
                  variant="primary"
                  disabled={isLoadingForm || !formProps.isValid || formProps.isSubmitting}
                  loading={isLoadingForm || formProps.isSubmitting}
                  className="w-50 mr-4"
                >
                  Сохранить
                </UDButton>

                <UDButton type="button" variant="secondary" disabled={isLoadingForm} onClick={backHandler} className="w-50">
                  Отменить
                </UDButton>
              </div>
            </TeamPlayerForm>

            <PreventNavigationModal />
          </form>
        );
      }}
    </Formik>
  );
};

export default PlayerForm;
